import { put, call, select } from 'redux-saga/effects';
import { jobApplicationActions } from '../redux/jobApplicationSlice';
import { PayloadAction } from '@reduxjs/toolkit';
import {
    IApplyToJobAction,
    ICreateJobApplicationCommentAction,
    IDeleteTagAction,
    IFetchActiveJobApplicationAction,
    ILoadNextOrPrevCandidateAction,
    ISaveForLaterAction,
    ISendChatMessageAction,
    ISetTagAction,
    ITransferCandidateAction,
    IUpdateJobApplicationStatusAction,
    IUpdateUserRequirementScoreAction,
    IScheduleInterviewAction,
    IUpdateCandidateInfoAction,
    IBulkRejectApplicationAction,
} from '../redux/interface';
import {
    applyToJobApi,
    createJobApplicationComment,
    deleteTag,
    duplicateJobApplicationApi,
    fetchJobApplication,
    fetchJobApplicationsStatisticsApi,
    saveforlaterJobApplication,
    sendChatMessage,
    applyTag,
    updateJobApplicationStatusApi,
    updateUserRequirementScore,
    scheduleInterviewApi,
    updateCandidateInfoApi,
    batchDeclineCandidatesApi,
} from '../../apis/jobApplication';
import {
    formatActiveJobApplication,
    formatJobApplicationsStatistics,
    formatScheduleInterviewData,
} from './manager';
import {
    getJobApplicationStatusLabel,
    getStatusColor,
    IJobApplication,
    IServerJobApplication,
    SnackbarUtils,
    TableIds,
    ITag,
    IServerJobCandidate,
} from '../../../common';
import { formatJobApplicationTableRow } from '../../tables/saga/manager';
import { jobApplicationSelectors } from '../redux';
import i18next from 'i18next';
import { globalNavigate, ITableState } from '../../../base';
import { RoutePaths } from '../../../routes/RoutePaths';
import {
    jobsActions,
    jobsSelectors,
    organizationSelectors,
    tablesActions,
    tablesSelectors,
    userSelectors,
} from '../../../actions';
import { convertJobApplicationToCandidate } from '../../jobs/saga/manager';

export function* applyNowSaga(action: PayloadAction<IApplyToJobAction>) {
    try {
        const { payload, jobId } = action.payload;
        const { response, error } = yield call(applyToJobApi, payload, jobId);
        if (response?.status === 200 || response?.status === 201) {
            yield put(jobApplicationActions.applyToJobSuccessful());
            yield call(
                SnackbarUtils.success,
                i18next.t('applyNow.jobApplicationSentSucessfully')
            );
        } else {
            yield call(SnackbarUtils.error, error.data?.[0]);
        }
    } catch (error) {
        console.log(`Error applying to job: ${error}`);
    } finally {
        yield put(jobApplicationActions.applyToJobDone());
    }
}

export function* fetchActiveJobApplication(
    action: PayloadAction<IFetchActiveJobApplicationAction>
) {
    try {
        const { id } = action.payload;
        yield put(
            jobApplicationActions.setActiveJobApplication({
                jobApplication: null,
            })
        );
        const { response } = yield call(fetchJobApplication, id);
        if (
            response?.status === 200 &&
            String(response?.data?.id) === String(id)
        ) {
            yield put(
                jobApplicationActions.setActiveJobApplication({
                    jobApplication: formatActiveJobApplication(response?.data),
                })
            );
        } else {
            console.log('Failed to fetch active job application');
        }
    } catch (error) {
        console.log(error);
    }
}

function* setActiveJobApplicationIfStillViewing(
    applicationData: IServerJobApplication
) {
    const activeJobApplication: IJobApplication | null = yield select(
        jobApplicationSelectors.getActiveJobApplication
    );
    if (
        activeJobApplication &&
        Number(activeJobApplication.id) === Number(applicationData.id)
    ) {
        yield put(
            jobApplicationActions.setActiveJobApplication({
                jobApplication: formatActiveJobApplication(applicationData),
            })
        );
    }
}

export function* updateJobApplicationStatus(
    action: PayloadAction<IUpdateJobApplicationStatusAction>
) {
    try {
        const { response, error } = yield call(
            updateJobApplicationStatusApi,
            action.payload
        );
        if (response?.status === 200) {
            // Update Table Data
            const tableData: Array<Record<string, string>> = yield select(
                tablesSelectors.getRowsData,
                TableIds.JOB_APPLICATIONS
            );
            const tableState: ITableState = yield select(
                tablesSelectors.getTablesState,
                TableIds.JOB_APPLICATIONS
            );
            const updatedTableData = tableData.map((row) => {
                if (row.id === response?.data.id) {
                    const updatedRow = { ...row };
                    updatedRow.statusLabel = getJobApplicationStatusLabel(
                        response.data.status
                    );
                    updatedRow.statusColor = getStatusColor(
                        response.data.status
                    );
                    return updatedRow;
                }
                return row;
            });
            yield put(
                tablesActions.setRowsDataById({
                    tableId: TableIds.JOB_APPLICATIONS,
                    data: updatedTableData,
                    page: tableState.currentPage,
                })
            );
            // Fetch the full job application to get updated candidate data
            const fullApplicationResponse = yield call(
                fetchJobApplication,
                response?.data.id
            );

            const fullData = fullApplicationResponse?.response?.data;

            // Update Active Job Application only if user is still viewing this application
            if (fullApplicationResponse?.response?.status === 200) {
                yield* setActiveJobApplicationIfStillViewing(fullData);

                // Convert job application to candidate format and update candidates menu
                const candidateData =
                    convertJobApplicationToCandidate(fullData);
                yield put(
                    jobsActions.updateActiveJobCandidate({
                        updatedCandidate: candidateData,
                        rejectedStepName: i18next.t(
                            'jobApplicationsPage.candidatesMenuRejectedCandidates',
                            {
                                defaultValue: 'Rejected Candidates',
                            }
                        ),
                        withdrawnStepName: i18next.t(
                            'jobApplicationsPage.candidatesMenuWithdrawnCandidates',
                            {
                                defaultValue: 'Withdrawn Candidates',
                            }
                        ),
                    })
                );
            } else if (response?.data) {
                // Fallback if fetching full application fails
                yield* setActiveJobApplicationIfStillViewing(response.data);
            }
            // Close Reject Application Modal
            yield put(
                jobApplicationActions.setIsRejectApplicationModalOpen(false)
            );
            // Show Success Snackbar
            yield call(
                SnackbarUtils.success,
                i18next.t(
                    'jobApplicationPage.applicationStatusUpdatedSuccessfully'
                )
            );
        } else {
            yield call(SnackbarUtils.error, error?.data?.[0]);
        }
    } catch (error) {
        console.log(`Error updating job application status: ${error}`); //TODO: Saga error handler
    } finally {
        yield put(jobApplicationActions.updateJobApplicationStatusDone());
    }
}

export function* fetchJobApplicationsStatistics() {
    try {
        const { response, error } = yield call(
            fetchJobApplicationsStatisticsApi
        );
        if (response?.status === 200) {
            yield put(
                jobApplicationActions.setJobApplicationsStatistics(
                    formatJobApplicationsStatistics(response?.data)
                )
            );
            yield put(
                jobApplicationActions.fetchJobApplicationsStatisticsDone()
            );
        } else {
            yield put(
                jobApplicationActions.fetchJobApplicationsStatisticsDone()
            );
            yield call(SnackbarUtils.error, error?.data?.[0]);
        }
    } catch (error) {
        yield put(jobApplicationActions.fetchJobApplicationsStatisticsDone());
        console.log(`Error fetching job applications statistics: ${error}`); //TODO: Saga error handler
    }
}

export function* createJobApplicationCommentSaga(
    action: PayloadAction<ICreateJobApplicationCommentAction>
) {
    try {
        const { text } = action.payload;
        const jobApplication: IJobApplication = yield select(
            jobApplicationSelectors.getActiveJobApplication
        );
        const { response } = yield call(
            createJobApplicationComment,
            jobApplication.id,
            text
        );
        if (response?.status === 200) {
            yield* setActiveJobApplicationIfStillViewing(response?.data);
            yield call(SnackbarUtils.success, 'Comment added successfully!');
        } else {
            yield call(
                SnackbarUtils.error,
                i18next.t('jobApplicationPage.failedToCreateComment')
            );
        }
    } catch (error) {
        console.log(`Failed to create comment: ${error}`); //TODO: Saga error handler
    } finally {
        yield put(jobApplicationActions.createJobApplicationCommentDone());
    }
}

export function* sendChatMessageSaga(
    action: PayloadAction<ISendChatMessageAction>
) {
    try {
        const { message } = action.payload;
        const jobApplication: IJobApplication = yield select(
            jobApplicationSelectors.getActiveJobApplication
        );
        const { response } = yield call(
            sendChatMessage,
            jobApplication.id,
            message
        );
        if (response?.status === 200 || response?.status === 201) {
            yield* setActiveJobApplicationIfStillViewing(response?.data);
        } else {
            yield call(
                SnackbarUtils.error,
                i18next.t('jobApplicationPage.failedToSendMessage')
            );
        }
    } catch (error) {
        console.log(`Failed to send message: ${error}`);
    } finally {
        yield put(jobApplicationActions.sendChatMessageDone());
    }
}

export function* saveForLaterSaga(action: PayloadAction<ISaveForLaterAction>) {
    try {
        const { id, isSaved } = action.payload;
        const jobApplication: IJobApplication = yield select(
            jobApplicationSelectors.getActiveJobApplication
        );
        let payloadId, payloadIsSaved;
        if (id) {
            payloadId = id;
            payloadIsSaved = isSaved;
        } else {
            payloadId = jobApplication.id;
            payloadIsSaved = jobApplication.isSaved;
        }
        const { response } = yield call(
            saveforlaterJobApplication,
            payloadId,
            payloadIsSaved
        );

        if (response?.status === 200) {
            if (!id) {
                yield put(
                    jobApplicationActions.setActiveJobApplication({
                        jobApplication: {
                            ...jobApplication,
                            isSaved: !jobApplication.isSaved,
                        },
                    })
                );
            } else {
                const tableData: Array<Record<string, string>> = yield select(
                    tablesSelectors.getRowsData,
                    TableIds.JOB_APPLICATIONS
                );
                const tableState: ITableState = yield select(
                    tablesSelectors.getTablesState,
                    TableIds.JOB_APPLICATIONS
                );
                const updatedTableData = tableData.map((row) => {
                    if (Number(row.id) === Number(id)) {
                        const updatedRow = { ...row };
                        updatedRow.isSavedIconName = isSaved
                            ? 'BookmarkButtonIcon'
                            : 'BookmarkFilledIcon';
                        return updatedRow;
                    }
                    return row;
                });
                yield put(
                    tablesActions.setRowsDataById({
                        tableId: TableIds.JOB_APPLICATIONS,
                        data: updatedTableData,
                        page: tableState.currentPage,
                    })
                );
                if (jobApplication && Number(jobApplication.id) === Number(id)) {
                    yield put(
                        jobApplicationActions.setActiveJobApplication({
                            jobApplication: {
                                ...jobApplication,
                                isSaved: !jobApplication.isSaved,
                            },
                        })
                    );
                }
            }
            yield call(
                SnackbarUtils.success,
                jobApplication?.isSaved || isSaved
                    ? i18next.t('jobApplicationPage.applicationUnsaved')
                    : i18next.t('jobApplicationPage.applicationSaved')
            );
        }
    } catch (error) {
        console.log(`Failed to save application for later: ${error}`); //TODO: Saga error handler
    }
}

export function* loadNextOrPrevCandidateSaga(
    action: PayloadAction<ILoadNextOrPrevCandidateAction>
) {
    try {
        const { candidateId, baseColors } = action.payload;
        const jobApplicationIds: number[] = yield select(
            jobApplicationSelectors.getJobApplicationsIds
        );
        const currentIndex = jobApplicationIds.indexOf(candidateId);
        const existingQueryParams = new URLSearchParams(location.search);
        globalNavigate(
            `${RoutePaths.jobApplications}/${candidateId}?${existingQueryParams.toString()}`
        );

        if (currentIndex > jobApplicationIds.length - 3) {
            yield put(
                tablesActions.fetchTableNextPage({
                    tableId: TableIds.JOB_APPLICATIONS,
                    baseColors,
                })
            );
        }
    } catch (error) {
        console.log(`Failed to fetch next candidate: ${error}`); //TODO: Saga error handler
    }
}

export function* applyTagToJobApplicationSaga(
    action: PayloadAction<ISetTagAction>
) {
    try {
        const { response, error } = yield call(applyTag, action.payload);
        if (response?.status === 201) {
            const activeJobApplication: IJobApplication = yield select(
                jobApplicationSelectors.getActiveJobApplication
            );
            const existingTags: Array<ITag> = yield select(
                organizationSelectors.getTags
            );
            if (action.payload.isInTableRow) {
                const tableData: Array<Record<string, string>> = yield select(
                    tablesSelectors.getRowsData,
                    TableIds.JOB_APPLICATIONS
                );
                const tableState: ITableState = yield select(
                    tablesSelectors.getTablesState,
                    TableIds.JOB_APPLICATIONS
                );
                const updatedTableData = tableData.map((row) => {
                    if (Number(row.id) === Number(action.payload.object_id)) {
                        const updatedRow = { ...row };
                        const appliedTagValues = existingTags.find(
                            (tag) => tag.tag_id === action.payload.tag
                        );
                        const newAppliedTag = {
                            id: response.data.id,
                            ...appliedTagValues,
                        };
                        updatedRow.appliedTags = [
                            ...row.appliedTags,
                            newAppliedTag,
                        ];
                        return updatedRow;
                    }
                    return row;
                });
                yield put(
                    tablesActions.setRowsDataById({
                        tableId: TableIds.JOB_APPLICATIONS,
                        data: updatedTableData,
                        page: tableState.currentPage,
                    })
                );
            } else {
                const tagToAdd = existingTags.find(
                    (tag) => tag.tag_id === action.payload.tag
                );
                const newTag: ITag = {
                    id: response.data.id,
                    tag_id: tagToAdd?.tag_id || action.payload.tag,
                    label: tagToAdd?.label || '',
                    color: tagToAdd?.color || '',
                };
                const updatedJobApplication: IJobApplication = {
                    ...activeJobApplication,
                    tags: [...activeJobApplication.tags, newTag],
                };
                yield put(
                    jobApplicationActions.setActiveJobApplication({
                        jobApplication: updatedJobApplication,
                    })
                );
            }

            // Update candidates menu with new tag
            // Fetch full application to get proper structure for conversion
            const fullApplicationResponse = yield call(
                fetchJobApplication,
                String(action.payload.object_id)
            );
            console.log('[applyTag] Fetched application for candidates menu:', {
                status: fullApplicationResponse?.response?.status,
                hasData: !!fullApplicationResponse?.response?.data,
                tags: fullApplicationResponse?.response?.data?.tags,
            });
            if (fullApplicationResponse?.response?.status === 200) {
                const candidateData = convertJobApplicationToCandidate(
                    fullApplicationResponse.response.data
                );
                console.log('[applyTag] Converted candidate data:', {
                    id: candidateData.id,
                    tags: candidateData.tags,
                });
                yield put(
                    jobsActions.updateActiveJobCandidate({
                        updatedCandidate: candidateData,
                        rejectedStepName: i18next.t(
                            'jobApplicationsPage.candidatesMenuRejectedCandidates',
                            {
                                defaultValue: 'Rejected Candidates',
                            }
                        ),
                        withdrawnStepName: i18next.t(
                            'jobApplicationsPage.candidatesMenuWithdrawnCandidates',
                            {
                                defaultValue: 'Withdrawn Candidates',
                            }
                        ),
                    })
                );
            }
        } else {
            yield call(SnackbarUtils.error, error?.data?.[0]);
        }
    } catch (error) {
        console.log(`Failed to set tag: ${error}`); //TODO: Saga error handler
    } finally {
        yield put(jobApplicationActions.applyTagToJobApplicationDone());
    }
}

export function* deleteTagFromJobApplicationSaga(
    action: PayloadAction<IDeleteTagAction>
) {
    try {
        const { id, isInTableRow, jobApplicationId } = action.payload;
        const { response, error } = yield call(deleteTag, id);
        // Check if response might have data (some APIs return 200 with data instead of 204)
        if (response?.status === 204 || response?.status === 200) {
            if (isInTableRow) {
                const tableData: Array<Record<string, string>> = yield select(
                    tablesSelectors.getRowsData,
                    TableIds.JOB_APPLICATIONS
                );
                const tableState: ITableState = yield select(
                    tablesSelectors.getTablesState,
                    TableIds.JOB_APPLICATIONS
                );
                const updatedTableData = tableData.map((row) => {
                    if (Number(row.id) === Number(jobApplicationId)) {
                        const updatedRow = { ...row };
                        updatedRow.appliedTags = row.appliedTags.filter(
                            (tag) => Number(tag.id) !== Number(id)
                        );
                        return updatedRow;
                    }
                    return row;
                });
                yield put(
                    tablesActions.setRowsDataById({
                        tableId: TableIds.JOB_APPLICATIONS,
                        data: updatedTableData,
                        page: tableState.currentPage,
                    })
                );
            } else {
                const activeJobApplication: IJobApplication = yield select(
                    jobApplicationSelectors.getActiveJobApplication
                );
                const updatedJobApplication = {
                    ...activeJobApplication,
                    tags: activeJobApplication.tags.filter(
                        (tag) => Number(tag.id) !== id
                    ),
                };
                yield put(
                    jobApplicationActions.setActiveJobApplication({
                        jobApplication: updatedJobApplication,
                    })
                );

                // Update candidates menu with removed tag
                // If response has data (status 200), use it; otherwise fetch
                if (response?.status === 200 && response?.data) {
                    const candidateData = convertJobApplicationToCandidate(
                        response.data
                    );
                    yield put(
                        jobsActions.updateActiveJobCandidate({
                            updatedCandidate: candidateData,
                            rejectedStepName: i18next.t(
                                'jobApplicationsPage.candidatesMenuRejectedCandidates',
                                {
                                    defaultValue: 'Rejected Candidates',
                                }
                            ),
                            withdrawnStepName: i18next.t(
                                'jobApplicationsPage.candidatesMenuWithdrawnCandidates',
                                {
                                    defaultValue: 'Withdrawn Candidates',
                                }
                            ),
                        })
                    );
                } else {
                    // Fetch to get server format needed for conversion
                    const fullApplicationResponse = yield call(
                        fetchJobApplication,
                        String(jobApplicationId)
                    );
                    if (fullApplicationResponse?.response?.status === 200) {
                        const candidateData = convertJobApplicationToCandidate(
                            fullApplicationResponse.response.data
                        );
                        yield put(
                            jobsActions.updateActiveJobCandidate({
                                updatedCandidate: candidateData,
                                rejectedStepName: i18next.t(
                                    'jobApplicationsPage.candidatesMenuRejectedCandidates',
                                    {
                                        defaultValue: 'Rejected Candidates',
                                    }
                                ),
                                withdrawnStepName: i18next.t(
                                    'jobApplicationsPage.candidatesMenuWithdrawnCandidates',
                                    {
                                        defaultValue: 'Withdrawn Candidates',
                                    }
                                ),
                            })
                        );
                    }
                }
            }
        } else {
            yield call(SnackbarUtils.error, error?.data?.[0]);
        }
    } catch (error) {
        console.log(`Failed to delete tag: ${error}`); //TODO: Saga error handler
    } finally {
        yield put(jobApplicationActions.deleteTagFromJobApplicationDone());
    }
}

export function* updateUserRequirementScoreSaga(
    action: PayloadAction<IUpdateUserRequirementScoreAction>
) {
    try {
        const { response, error } = yield call(
            updateUserRequirementScore,
            action.payload
        );
        if (response?.status === 200 || response?.status === 201) {
            const activeJobApplication: IJobApplication = yield select(
                jobApplicationSelectors.getActiveJobApplication
            );

            if (activeJobApplication) {
                const myUser: ReturnType<typeof userSelectors.getMyUser> = yield select(
                    userSelectors.getMyUser
                );
                const userId = String(myUser?.id);
                const jobApplicationScoreId = response.data.job_application_score;
                const aiScores = activeJobApplication.requirements?.['ai'] ?? {};

                let importanceLevel: string | null = null;
                let reqName = '';
                for (const [level, reqs] of Object.entries(aiScores)) {
                    const found = reqs.find((r) => r.id === jobApplicationScoreId);
                    if (found) {
                        importanceLevel = level;
                        reqName = found.name;
                        break;
                    }
                }

                if (importanceLevel && userId) {
                    const userLevelScores =
                        activeJobApplication.requirements?.[userId]?.[importanceLevel] ?? [];
                    const existingIdx = userLevelScores.findIndex(
                        (r) => r.job_application_score === jobApplicationScoreId
                    );
                    const newEntry = {
                        id: response.data.id,
                        score: response.data.score,
                        name: reqName,
                        reason: response.data.reason ?? '',
                        job_application_score: jobApplicationScoreId,
                    };
                    const updatedUserLevelScores = [...userLevelScores];
                    if (existingIdx >= 0) {
                        updatedUserLevelScores[existingIdx] = newEntry;
                    } else {
                        updatedUserLevelScores.push(newEntry);
                    }

                    yield put(
                        jobApplicationActions.setActiveJobApplication({
                            jobApplication: {
                                ...activeJobApplication,
                                requirements: {
                                    ...activeJobApplication.requirements,
                                    [userId]: {
                                        ...activeJobApplication.requirements?.[userId],
                                        [importanceLevel]: updatedUserLevelScores,
                                    },
                                },
                            },
                        })
                    );
                }
            }

            yield call(
                SnackbarUtils.success,
                i18next.t(
                    'jobApplicationPage.requirementScoreUpdatedSuccessfully'
                )
            );
        } else {
            yield call(
                SnackbarUtils.error,
                error?.data?.['non_field_errors']?.[0]
            );
        }
    } catch (error) {
        console.log(`Failed to update user requirement score: ${error}`);
    }
}

export function* transferCandidateSaga(
    action: PayloadAction<ITransferCandidateAction>
) {
    try {
        const { applicationId, jobId } = action.payload;
        const { response, error } = yield call(
            duplicateJobApplicationApi,
            applicationId,
            jobId
        );
        if (response?.status === 201) {
            const newRow = formatJobApplicationTableRow(
                response.data.new_application
            );
            yield put(
                tablesActions.upsertTableRow({
                    tableId: TableIds.JOB_APPLICATIONS,
                    row: newRow,
                })
            );
            yield put(
                jobApplicationActions.setIsTransferCandidateModalOpen(false)
            );
            yield call(
                SnackbarUtils.success,
                i18next.t('transferCandidate.transferSuccess')
            );
        } else {
            yield call(
                SnackbarUtils.error,
                error?.data?.[0] ||
                    i18next.t('transferCandidate.transferFailed')
            );
        }
    } catch (error) {
        console.log(`Failed to transfer candidate: ${error}`);
    } finally {
        yield put(jobApplicationActions.transferCandidateDone());
    }
}

export function* scheduleInterviewSaga(
    action: PayloadAction<IScheduleInterviewAction>
) {
    try {
        const { jobApplicationId, interviewData } = action.payload;

        const formattedData = formatScheduleInterviewData(
            parseInt(interviewData.interviewDuration.value),
            interviewData.availability
        );

        const { response, error } = yield call(
            scheduleInterviewApi,
            jobApplicationId,
            formattedData
        );

        if (response?.status === 200 || response?.status === 201) {
            yield put(jobApplicationActions.scheduleInterviewSuccess());

            const activeJobApplication: IJobApplication | null = yield select(
                jobApplicationSelectors.getActiveJobApplication
            );

            if (activeJobApplication) {
                yield put(
                    jobApplicationActions.fetchJobActiveApplication({
                        id: activeJobApplication.id.toString(),
                    })
                );
            }
        } else {
            yield put(jobApplicationActions.scheduleInterviewFailed());
            yield call(
                SnackbarUtils.error,
                error?.data?.message ||
                    i18next.t('scheduleInterview.failedToScheduleInterview')
            );
        }
    } catch (error) {
        yield put(jobApplicationActions.scheduleInterviewFailed());
        yield call(
            SnackbarUtils.error,
            i18next.t('scheduleInterview.failedToScheduleInterview')
        );
    }
}

export function* updateCandidateInfoSaga(
    action: PayloadAction<IUpdateCandidateInfoAction>
) {
    try {
        const { jobApplicationId, firstName, lastName, phone, email } =
            action.payload;

        const { response, error } = yield call(
            updateCandidateInfoApi,
            jobApplicationId,
            {
                first_name: firstName,
                last_name: lastName,
                phone,
                email,
            }
        );

        if (response?.status === 200) {
            yield call(
                SnackbarUtils.success,
                i18next.t('jobApplicationPage.candidateInfoUpdated')
            );

            // Update the active job application in Redux
            yield put(
                jobApplicationActions.updateCandidateInfoSuccess({
                    firstName,
                    lastName,
                    phone,
                    email,
                })
            );

            // Update the table row
            try {
                yield put(
                    tablesActions.updateTableRowById({
                        tableId: TableIds.JOB_APPLICATIONS,
                        newTableRow: {
                            id: jobApplicationId,
                            name: `${firstName} ${lastName}`,
                        },
                    })
                );
            } catch (tableUpdateError) {
                console.error('Error updating table:', tableUpdateError);
            }

            // Update the candidates menu (View Candidates)
            try {
                const activeJob = yield select(jobsSelectors.getActiveJob);
                if (activeJob) {
                    yield put(
                        jobsActions.updateActiveJobCandidateName({
                            candidateId: jobApplicationId,
                            firstName,
                            lastName,
                            email,
                        })
                    );
                }
            } catch (candidatesUpdateError) {
                console.error('Error updating candidates:', candidatesUpdateError);
            }

            yield put(jobApplicationActions.updateCandidateInfoDone());
        } else {
            yield call(
                SnackbarUtils.error,
                error?.data?.message ||
                    i18next.t('jobApplicationPage.failedToUpdateCandidateInfo')
            );
            yield put(jobApplicationActions.updateCandidateInfoDone());
        }
    } catch (error) {
        yield call(
            SnackbarUtils.error,
            i18next.t('jobApplicationPage.failedToUpdateCandidateInfo')
        );
        yield put(jobApplicationActions.updateCandidateInfoDone());
    }
}

export function* bulkRejectApplicationSaga(
    action: PayloadAction<IBulkRejectApplicationAction>
) {
    try {
        const { applicationIds, reasonOption, reasonText } = action.payload;
        
        const { response, error } = yield call(batchDeclineCandidatesApi, {
            job_application_ids: applicationIds,
            reject_reason: reasonOption?.value ? Number(reasonOption.value) : undefined,
            text: reasonText || undefined,
        });

        if (response?.status === 202) {
            // Close the modal
            yield put(
                jobApplicationActions.setIsBulkRejectApplicationModalOpen(false)
            );

            // Clear selected rows
            const tableState: ITableState = yield select(
                tablesSelectors.getTablesState,
                TableIds.JOB_APPLICATIONS
            );
            yield put(
                tablesActions.setTableStateById({
                    tableId: TableIds.JOB_APPLICATIONS,
                    tableState: {
                        ...tableState,
                        selectedRows: {},
                    },
                })
            );

            // Show success message
            yield call(
                SnackbarUtils.success,
                i18next.t('jobApplicationPage.bulkRejectInProgress', {
                    count: applicationIds.length,
                })
            );
        } else {
            yield call(SnackbarUtils.error, error?.data?.[0] || error?.data?.message);
        }
    } catch (error) {
        console.log(`Error bulk rejecting applications: ${error}`);
        yield call(
            SnackbarUtils.error,
            i18next.t('jobApplicationPage.bulkRejectFailed')
        );
    } finally {
        yield put(jobApplicationActions.bulkRejectApplicationDone());
    }
}
